import React from 'react';
import { RouterProvider, Route, createRoutesFromElements, createBrowserRouter, Outlet } from 'react-router-dom';

import { NavigateTo, getPath, Path, paths } from '@neo.tax/shared';

import { App } from '../App';
import { createLazyComponent } from '../utils';

const MaintenanceMode = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).MaintenanceMode,
  }),
  'MaintenanceMode',
);
const Login = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).Login,
  }),
  'Login',
);
const Signup = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).Signup,
  }),
  'Signup',
);
const ResetPassword = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).ResetPassword,
  }),
  'ResetPassword',
);
const VerifyEmail = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).VerifyEmail,
  }),
  'VerifyEmail',
);
const VerifyEmailSent = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).VerifyEmailSent,
  }),
  'VerifyEmailSent',
);
const AcceptInvitation = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).AcceptInvitation,
  }),
  'AcceptInvitation',
);
const EnterpriseSso = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).EnterpriseSso,
  }),
  'EnterpriseSso',
);
const Logout = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).Logout,
  }),
  'Logout',
);
const Profile = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/onboarding')).Profile,
  }),
  'Profile',
);
const Companies = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/company')).Companies,
  }),
  'Companies',
);
const FiscalYearHome = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/company')).FiscalYearHome,
  }),
  'FiscalYearHome',
);
// R&D
const RdHome = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/rd')).RdHome,
  }),
  'RdHome',
);
const CompanyInfo = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/rd')).CompanyInfo,
  }),
  'CompanyInfo',
);
const RdDataSources = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/rd')).RdDataSources,
  }),
  'RdDataSources',
);
const RdProjects = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/rd')).RdProjects,
  }),
  'RdProjects',
);
const PayrollQres = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/rd')).PayrollQres,
  }),
  'PayrollQres',
);
const AccountingQres = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/rd')).AccountingQres,
  }),
  'AccountingQres',
);
const CreditCalculation = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/rd')).CreditCalculation,
  }),
  'CreditCalculation',
);
const ExportTax = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/rd')).ExportTax,
  }),
  'ExportTax',
);
const SredHome = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/sred')).SredHome,
  }),
  'SredHome',
);
const SredProjects = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/sred')).SredProjects,
  }),
  'SredProjects',
);
const SredPayrollExpenses = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/ca/rd/pages')).SredPayrollExpenses,
  }),
  'SredPayrollExpenses',
);
// ASC
const AscHome = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/asc')).AscHome,
  }),
  'AscHome',
);
const AscProjects = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/asc')).AscProjects,
  }),
  'AscProjects',
);
const AscDataSources = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/asc')).AscDataSources,
  }),
  'AscDataSources',
);
const AscWages = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/asc')).AscWages,
  }),
  'AscWages',
);
const ExportAsc = createLazyComponent(
  async () => ({
    default: (await import('@neo.tax/asc')).ExportAsc,
  }),
  'ExportAsc',
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<App />}>
      <Route path={paths[Path.MAINTENANCE_MODE]} element={<MaintenanceMode />} />
      <Route path={paths[Path.LOGIN]} element={<Login />} />
      <Route path={paths[Path.SIGNUP]} element={<Signup />} />
      <Route path={paths[Path.RESET_PASSWORD]} element={<ResetPassword />} />
      <Route path={paths[Path.VERIFY_EMAIL]} element={<VerifyEmail />} />
      <Route path={paths[Path.VERIFY_EMAIL_SENT]} element={<VerifyEmailSent />} />
      <Route path={paths[Path.ACCEPT_INVITATION]} element={<AcceptInvitation />} />
      <Route path={paths[Path.ENTERPRISE_SSO]} element={<EnterpriseSso />} />
      <Route path={paths[Path.PROFILE]} element={<Profile />} />
      <Route path={paths[Path.LOGOUT]} element={<Logout />} />
      <Route path={paths[Path.COMPANIES]} element={<Companies />}>
        <Route path=":companyId" element={<Outlet />}>
          <Route path=":product" element={<Outlet />}>
            <Route path=":fiscalYearId" element={<Outlet />}>
              <Route path=":periodName" element={<FiscalYearHome />}>
                {/* R&D */}
                <Route element={<RdHome />}>
                  <Route index path={paths[Path.COMPANY_INFO]} element={<CompanyInfo />} />
                  <Route path={paths[Path.RD_PROJECTS]}>
                    <Route index element={<RdProjects />} />
                    <Route path=":rdProjectId" element={<RdProjects />} />
                  </Route>
                  <Route path={paths[Path.RD_DATA_SOURCES]} element={<RdDataSources />} />
                  <Route path={paths[Path.PAYROLL_QRE]} element={<PayrollQres />} />
                  <Route path={paths[Path.ACCOUNTING_QRE]} element={<AccountingQres />} />
                  <Route path={paths[Path.CREDIT_CALCULATION]} element={<CreditCalculation />} />
                  <Route path={paths[Path.EXPORT_TAX]} element={<ExportTax />} />
                </Route>
                {/* SR&ED */}
                <Route element={<SredHome />}>
                  <Route path={paths[Path.SRED_PROJECTS]}>
                    <Route index element={<SredProjects />} />
                    <Route path=":sredProjectId" element={<SredProjects />} />
                  </Route>
                  <Route path={paths[Path.SRED_PAYROLL_EXPENSES]} element={<SredPayrollExpenses />} />
                </Route>
                {/* ASC */}
                <Route element={<AscHome />}>
                  <Route index path={paths[Path.ASC_DATA_SOURCES]} element={<AscDataSources />} />
                  <Route path={paths[Path.ASC_PROJECTS]}>
                    <Route index element={<AscProjects />} />
                    <Route path=":ascProjectId" element={<AscProjects />} />
                  </Route>
                  <Route path={paths[Path.ASC_WAGES]} element={<AscWages />} />
                  <Route path={paths[Path.EXPORT_ASC]} element={<ExportAsc />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<NavigateTo to={getPath({ path: Path.COMPANIES })} />} />
      </Route>
      <Route path="*" element={<NavigateTo to={getPath({ path: Path.LOGIN })} />} />
    </Route>,
  ),
);

/**
 * Highest level router. Use this to route between modules.
 */
export const AppRouter = (): React.ReactElement => <RouterProvider router={router} />;
